import React, { useState } from 'react';
import { ExternalLink, Bookmark, HelpCircle } from "lucide-react";
import PageHeader from './PageHeader';
import ShadesDisplay from './ShadeDisplay';
import PaletteDisplay from './PaletteDisplay';
import CompactExportDropdown from './CompactExportDropdown';
import Tooltip from './ui/Tooltip';
import { hexToHsl, hslToHex, hexToOKLCH, oklchToHex, hexToLCH, lchToHex, hexToRgb, rgbToHex } from '../lib/colorUtils';

const PaletteTabs = ({
    palettes = {},
    shades = [],
    selectedColor,
    onColorClick,
    visibleFields = [],
    colorBlindnessType = 'none',
    isDark = false,
    addColorToPalette,
    setVisualizerColors,
    setVisualizerColorsWithAlert,
    savePalette,
    addColorToCustomPalette,
    initialTab = 'palettes'
}) => {
    const [activeTab, setActiveTab] = useState(initialTab);

    const toSentenceCase = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const handleExportToVisualizer = (colors, paletteName, event) => {
        const colorObjects = colors.map((color, index) => ({
            id: `${paletteName.toLowerCase().replace(/\s+/g, '-')}-${index}`,
            value: color
        }));

        // Pass the clicked button element as the trigger
        setVisualizerColors(colorObjects, event.target);
        console.log(`Exported ${paletteName} to visualizer with ${colors.length} colors:`, colors);
    };

    // Advanced harmony generation functions (same as before)
    const generateAdvancedHarmonies = (baseColor) => {
        const [h, s, l] = hexToHsl(baseColor);

        // Helper function to ensure hue is within 0-360 range
        const normalizeHue = (hue) => ((hue % 360) + 360) % 360;

        // Helper function to create variations with saturation and lightness adjustments
        const createColorVariation = (hue, satMod = 0, lightMod = 0) => {
            const newSat = Math.max(10, Math.min(90, s + satMod));
            const newLight = Math.max(15, Math.min(85, l + lightMod));
            return hslToHex(normalizeHue(hue), newSat, newLight);
        };

        return {
            // All the same harmony generation code as before...
            goldenRatio: (() => {
                const golden = 137.508;
                const colors = [baseColor];
                for (let i = 1; i < 12; i++) {
                    const newHue = h + (golden * i);
                    const satVar = Math.sin(i * 0.5) * 15;
                    const lightVar = Math.cos(i * 0.7) * 20;
                    colors.push(createColorVariation(newHue, satVar, lightVar));
                }
                return colors;
            })(),
            doubleComplementary: (() => {
                const baseComplement = h + 180;
                const offset = 30;
                return [
                    baseColor,
                    createColorVariation(h + offset, 5, -10),
                    createColorVariation(h - offset, -5, 10),
                    createColorVariation(baseComplement, 0, 0),
                    createColorVariation(baseComplement + offset, 10, -15),
                    createColorVariation(baseComplement - offset, -10, 15),
                    createColorVariation(h + offset * 2, 15, -5),
                    createColorVariation(h - offset * 2, -15, 5),
                    createColorVariation(baseComplement + offset * 2, 20, -20),
                    createColorVariation(baseComplement - offset * 2, -20, 20)
                ];
            })(),

            // Rainbow Spectrum Expansion (14 colors)
            rainbowSpectrum: (() => {
                const colors = [baseColor];
                const steps = 13;
                const hueStep = 360 / steps;

                for (let i = 1; i < 14; i++) {
                    const newHue = h + (hueStep * i);
                    const saturationWave = Math.sin((i / steps) * Math.PI * 2) * 20;
                    const lightnessWave = Math.cos((i / steps) * Math.PI * 3) * 15;
                    colors.push(createColorVariation(newHue, saturationWave, lightnessWave));
                }
                return colors;
            })(),

            // Advanced Triadic Plus (9 colors)
            advancedTriadic: (() => {
                const primary = h;
                const secondary = h + 120;
                const tertiary = h + 240;

                return [
                    baseColor,
                    createColorVariation(primary, 0, 20),
                    createColorVariation(primary, 0, -20),
                    createColorVariation(secondary, 10, 0),
                    createColorVariation(secondary, -10, 15),
                    createColorVariation(secondary, 5, -15),
                    createColorVariation(tertiary, -5, 10),
                    createColorVariation(tertiary, 15, -10),
                    createColorVariation(tertiary, -15, 25)
                ];
            })(),

            // Fibonacci Harmony (8 colors)
            fibonacci: (() => {
                const fibSequence = [1, 1, 2, 3, 5, 8, 13, 21];
                const colors = [baseColor];

                for (let i = 1; i < 8; i++) {
                    const hueShift = (fibSequence[i] * 23.5) % 360;
                    const satMod = (fibSequence[i] % 4 - 2) * 12;
                    const lightMod = (fibSequence[i] % 3 - 1) * 18;
                    colors.push(createColorVariation(h + hueShift, satMod, lightMod));
                }
                return colors;
            })(),

            // Warm-Cool Gradient Bridge (16 colors)
            warmCoolBridge: (() => {
                const colors = [];
                const warmStart = h;

                for (let i = 0; i < 16; i++) {
                    const progress = i / 15;
                    const currentHue = warmStart + (progress * 180);

                    const satWave = Math.sin(progress * Math.PI * 2) * 25;
                    const lightWave = Math.cos(progress * Math.PI * 1.5) * 30;

                    colors.push(createColorVariation(currentHue, satWave, lightWave));
                }
                return colors;
            })(),

            // Square Harmony Extended (12 colors)
            squareHarmony: (() => {
                const colors = [];
                const angles = [0, 90, 180, 270]; // Square harmony base

                angles.forEach(angle => {
                    const baseHue = h + angle;
                    colors.push(createColorVariation(baseHue, 0, 0)); // Original
                    colors.push(createColorVariation(baseHue, 20, -15)); // Vivid
                    colors.push(createColorVariation(baseHue, -15, 25)); // Pastel
                });
                return colors;
            })(),

            // Pentad Harmony (15 colors)
            pentadHarmony: (() => {
                const colors = [];
                const angles = [0, 72, 144, 216, 288]; // Pentagon angles

                angles.forEach(angle => {
                    const baseHue = h + angle;
                    colors.push(createColorVariation(baseHue, 0, 0));
                    colors.push(createColorVariation(baseHue, 15, -20));
                    colors.push(createColorVariation(baseHue, -10, 25));
                });
                return colors;
            })(),

            // Nature Inspired Palette (10 colors)
            natureInspired: (() => {
                const earthTones = [
                    { hue: h, sat: 0, light: 0 }, // Base
                    { hue: h + 25, sat: 10, light: 15 }, // Sky
                    { hue: h + 60, sat: 20, light: -10 }, // Forest
                    { hue: h + 90, sat: 15, light: 20 }, // Grass
                    { hue: h + 120, sat: -5, light: -15 }, // Ocean
                    { hue: h + 180, sat: 25, light: 5 }, // Sunset
                    { hue: h + 200, sat: -10, light: 30 }, // Cloud
                    { hue: h + 240, sat: 30, light: -25 }, // Mountain
                    { hue: h + 300, sat: 20, light: 10 }, // Flower
                    { hue: h + 330, sat: 5, light: -20 } // Earth
                ];

                return earthTones.map(tone =>
                    createColorVariation(tone.hue, tone.sat, tone.light)
                );
            })(),

            // Vintage Film Palette (8 colors)
            vintageFilm: (() => {
                const filmTones = [
                    { hue: h, sat: -20, light: 0 }, // Base desaturated
                    { hue: h + 15, sat: -15, light: 10 }, // Warm highlight
                    { hue: h + 30, sat: -10, light: -15 }, // Amber shadow
                    { hue: h + 180, sat: -25, light: 5 }, // Cool complement
                    { hue: h + 200, sat: -20, light: 20 }, // Faded blue
                    { hue: h + 45, sat: -5, light: -10 }, // Sepia tone
                    { hue: h + 270, sat: -30, light: 15 }, // Muted purple
                    { hue: h + 60, sat: -12, light: -5 } // Olive green
                ];

                return filmTones.map(tone =>
                    createColorVariation(tone.hue, tone.sat, tone.light)
                );
            })(),

            // Neon Cyberpunk (6 colors)
            neonCyberpunk: (() => {
                return [
                    baseColor,
                    createColorVariation(h + 180, 40, -30), // Electric complement
                    createColorVariation(h + 60, 45, -25), // Neon green
                    createColorVariation(h + 300, 50, -20), // Hot pink
                    createColorVariation(h + 240, 35, -35), // Electric blue
                    createColorVariation(h + 30, 40, -15) // Cyber orange
                ];
            })(),

            // Pastel Dream (8 colors)
            pastelDream: (() => {
                const pastelColors = [];
                for (let i = 0; i < 8; i++) {
                    const hueShift = (360 / 8) * i;
                    pastelColors.push(createColorVariation(h + hueShift, -30, 35));
                }
                return pastelColors;
            })(),

            // Monochromatic Extended (10 colors)
            monochromaticExtended: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const lightness = -40 + (i * 8); // From dark to light
                    const saturation = Math.sin((i / 9) * Math.PI) * 20; // Wave saturation
                    colors.push(createColorVariation(h, saturation, lightness));
                }
                return colors;
            })(),

            // Analogous Extended (12 colors)
            analogousExtended: (() => {
                const colors = [];
                for (let i = 0; i < 12; i++) {
                    const hueShift = -60 + (i * 10); // 120 degree spread
                    const satMod = Math.cos((i / 11) * Math.PI * 2) * 15;
                    const lightMod = Math.sin((i / 11) * Math.PI) * 20;
                    colors.push(createColorVariation(h + hueShift, satMod, lightMod));
                }
                return colors;
            })(),

            // Split Complementary Plus (9 colors)
            splitComplementaryPlus: (() => {
                const base = h;
                const comp1 = h + 150;
                const comp2 = h + 210;

                return [
                    baseColor,
                    createColorVariation(base, 10, 15),
                    createColorVariation(base, -10, -15),
                    createColorVariation(comp1, 0, 0),
                    createColorVariation(comp1, 15, -20),
                    createColorVariation(comp1, -15, 20),
                    createColorVariation(comp2, 0, 0),
                    createColorVariation(comp2, 20, -15),
                    createColorVariation(comp2, -20, 15)
                ];
            })(),

            materialDesign: (() => {
                const materialTones = [
                    { hue: h, sat: 0, light: 0 },
                    { hue: h, sat: 5, light: 40 },
                    { hue: h, sat: 10, light: 25 },
                    { hue: h, sat: 15, light: 10 },
                    { hue: h, sat: 20, light: -5 },
                    { hue: h, sat: 25, light: -15 },
                    { hue: h, sat: 30, light: -25 },
                    { hue: h, sat: 35, light: -35 },
                    { hue: h, sat: 40, light: -45 },
                    { hue: h + 60, sat: 45, light: -10 }
                ];
                return materialTones.map(tone =>
                    createColorVariation(tone.hue, tone.sat, tone.light)
                );
            })(),

            // Hexadic Harmony (6 colors)
            hexadic: (() => {
                const angles = [0, 60, 120, 180, 240, 300];
                return angles.map(angle =>
                    createColorVariation(h + angle, 0, 0)
                );
            })(),

            // Neutral with Accent (8 colors)
            neutralAccent: (() => {
                const neutrals = [];
                // Generate neutral grays
                for (let i = 0; i < 6; i++) {
                    neutrals.push(hslToHex(h, 5, 15 + (i * 14))); // Very low saturation
                }
                // Add accent colors
                neutrals.push(baseColor); // Primary accent
                neutrals.push(createColorVariation(h + 180, 20, -10)); // Secondary accent
                return neutrals;
            })(),

            // Saturation Gradient (10 colors)
            saturationGradient: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const newSat = 10 + (i * 10); // 10% to 100%
                    colors.push(hslToHex(h, newSat, l));
                }
                return colors;
            })(),

            // 60-30-10 Rule (4 colors with usage guidance)
            sixtyThirtyTen: (() => {
                return [
                    baseColor, // 60% - Dominant
                    createColorVariation(h + 30, -10, 10), // 30% - Secondary
                    createColorVariation(h + 180, 20, -15), // 10% - Accent 1
                    createColorVariation(h + 210, 25, -20) // 10% - Accent 2
                ];
            })(),

            // Tints, Tones, and Shades (12 colors)
            tintsToneShades: (() => {
                return [
                    // Tints (lighter + less saturated)
                    baseColor,
                    createColorVariation(h, -10, 20),
                    createColorVariation(h, -20, 35),
                    createColorVariation(h, -30, 50),
                    // Tones (desaturated)
                    createColorVariation(h, -20, 0),
                    createColorVariation(h, -35, 0),
                    createColorVariation(h, -50, 0),
                    createColorVariation(h, -65, 0),
                    // Shades (darker)
                    createColorVariation(h, 0, -20),
                    createColorVariation(h, 5, -35),
                    createColorVariation(h, 10, -50),
                    createColorVariation(h, 15, -65)
                ];
            })(),

            // Warm Palette (6 colors)
            warmPalette: (() => {
                const warmHues = [0, 15, 30, 45, 330, 345];
                return warmHues.map(offset =>
                    createColorVariation(h + offset, 10, 0)
                );
            })(),

            // Cool Palette (6 colors)
            coolPalette: (() => {
                const coolHues = [120, 150, 180, 210, 240, 270];
                return coolHues.map(offset =>
                    createColorVariation(h + offset, 10, 0)
                );
            })(),

            // Accented Analogous (6 colors)
            accentedAnalogous: (() => {
                return [
                    createColorVariation(h - 30, 0, 0),
                    baseColor,
                    createColorVariation(h + 30, 0, 0),
                    createColorVariation(h + 180, 0, 0), // Accent
                    createColorVariation(h - 30, 10, 15),
                    createColorVariation(h + 30, -10, -15)
                ];
            })(),

            // Near-Complementary (5 colors)
            nearComplementary: (() => {
                return [
                    baseColor,
                    createColorVariation(h + 165, 0, 0),
                    createColorVariation(h + 195, 0, 0),
                    createColorVariation(h + 165, 15, -15),
                    createColorVariation(h + 195, -15, 15)
                ];
            })(),

            // Spring Palette (6 colors)
            springPalette: (() => {
                const springHues = [45, 60, 90, 120, 150, 30];
                return springHues.map(offset =>
                    createColorVariation(h + offset, 15, 25) // Light, bright
                );
            })(),

            // Summer Palette (6 colors)
            summerPalette: (() => {
                const summerHues = [180, 200, 210, 240, 270, 190];
                return summerHues.map(offset =>
                    createColorVariation(h + offset, -10, 30) // Light, muted
                );
            })(),

            // Autumn Palette (6 colors)
            autumnPalette: (() => {
                const autumnHues = [0, 20, 35, 50, 10, 330];
                return autumnHues.map(offset =>
                    createColorVariation(h + offset, -15, -20) // Dark, muted
                );
            })(),

            // Winter Palette (6 colors)
            winterPalette: (() => {
                const winterHues = [180, 210, 240, 270, 300, 200];
                return winterHues.map(offset =>
                    createColorVariation(h + offset, 20, -25) // High contrast, cool
                );
            })(),

            // Brand System (9 colors)
            brandSystem: (() => {
                return [
                    // Primary colors
                    baseColor,
                    createColorVariation(h, 10, 15), // Light variant
                    createColorVariation(h, -10, -20), // Dark variant
                    // Secondary colors
                    createColorVariation(h + 30, 0, 0),
                    createColorVariation(h + 180, 0, 0),
                    // Tertiary/Accent colors
                    createColorVariation(h + 60, 15, 10),
                    createColorVariation(h + 270, -10, -5),
                    createColorVariation(h + 150, 20, -15),
                    createColorVariation(h + 210, -5, 20)
                ];
            })(),

            // ========== PERCEPTUAL & SCIENCE-BASED PALETTES ==========

            // OKLCH Uniform Spacing (10 colors) - Perceptually uniform hue steps
            oklchUniform: (() => {
                const [oklchL, oklchC, oklchH] = hexToOKLCH(baseColor);
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const newH = (oklchH + (i * 36)) % 360;
                    try {
                        colors.push(oklchToHex(oklchL, oklchC, newH));
                    } catch {
                        colors.push(createColorVariation(h + (i * 36), 0, 0));
                    }
                }
                return colors;
            })(),

            // OKLCH Lightness Scale (8 colors) - Perceptually uniform lightness
            oklchLightnessScale: (() => {
                const [, oklchC, oklchH] = hexToOKLCH(baseColor);
                const colors = [];
                for (let i = 0; i < 8; i++) {
                    const newL = 0.2 + (i * 0.1);
                    try {
                        colors.push(oklchToHex(newL, oklchC, oklchH));
                    } catch {
                        colors.push(createColorVariation(h, 0, -40 + (i * 10)));
                    }
                }
                return colors;
            })(),

            // LAB Color Space Gradient (10 colors)
            labGradient: (() => {
                const [lchL, lchC, lchH] = hexToLCH(baseColor);
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const newL = Math.max(10, Math.min(90, lchL - 40 + (i * 8)));
                    try {
                        colors.push(lchToHex(newL, lchC, lchH));
                    } catch {
                        colors.push(createColorVariation(h, 0, -40 + (i * 8)));
                    }
                }
                return colors;
            })(),

            // Bezier Curve Interpolation (12 colors) - Smooth color transitions
            bezierCurve: (() => {
                const colors = [baseColor];
                const controlH1 = h + 60;
                const controlH2 = h + 180;
                const endH = h + 300;

                for (let t = 0.1; t <= 1; t += 0.1) {
                    const bezierH =
                        Math.pow(1-t, 3) * h +
                        3 * Math.pow(1-t, 2) * t * controlH1 +
                        3 * (1-t) * Math.pow(t, 2) * controlH2 +
                        Math.pow(t, 3) * endH;

                    const satMod = Math.sin(t * Math.PI) * 20;
                    const lightMod = Math.cos(t * Math.PI) * 25;
                    colors.push(createColorVariation(bezierH, satMod, lightMod));
                }
                return colors;
            })(),

            // Maximum Chroma (8 colors) - Most saturated colors possible
            maximumChroma: (() => {
                const angles = [0, 45, 90, 135, 180, 225, 270, 315];
                return angles.map(angle =>
                    hslToHex((h + angle) % 360, 100, 50)
                );
            })(),

            // Itten Contrast Harmonies (7 colors) - Based on Johannes Itten's color theory
            ittenContrast: (() => {
                return [
                    baseColor, // Hue
                    createColorVariation(h, 0, -30), // Light-dark
                    createColorVariation(h, 0, 30), // Light-dark opposite
                    createColorVariation(h + 30, 0, 0), // Warm-cool (warm side)
                    createColorVariation(h + 180, 0, 0), // Complementary
                    createColorVariation(h, -40, 0), // Saturation contrast
                    createColorVariation(h, 40, 0) // Saturation contrast opposite
                ];
            })(),

            // ========== DATA VISUALIZATION PALETTES ==========

            // Sequential Single Hue (9 colors) - For ordered data
            sequentialSingleHue: (() => {
                const colors = [];
                for (let i = 0; i < 9; i++) {
                    const lightness = 85 - (i * 8);
                    const saturation = Math.max(20, s - (i * 3));
                    colors.push(hslToHex(h, saturation, lightness));
                }
                return colors;
            })(),

            // Categorical Chevron (12 colors) - Maximum distinction for categories
            categoricalChevron: (() => {
                const hues = [0, 120, 240, 60, 180, 300, 30, 150, 270, 90, 210, 330];
                return hues.map(offset => {
                    const newH = (h + offset) % 360;
                    const satMod = (offset % 2 === 0) ? 10 : -10;
                    const lightMod = (offset % 3 === 0) ? 10 : -5;
                    return createColorVariation(newH, satMod, lightMod);
                });
            })(),

            // Diverging Palette (11 colors) - Two hues diverging from neutral
            divergingPalette: (() => {
                const colors = [];
                const hue1 = h;
                const hue2 = (h + 180) % 360;

                for (let i = 0; i < 5; i++) {
                    colors.push(hslToHex(hue1, s - (i * 10), l + (i * 8)));
                }
                colors.push(hslToHex(h, 5, 50)); // Neutral center
                for (let i = 4; i >= 0; i--) {
                    colors.push(hslToHex(hue2, s - (i * 10), l + (i * 8)));
                }
                return colors;
            })(),

            // Qualitative Maximum Distance (8 colors) - Perceptually distinct
            qualitativeMaxDist: (() => {
                const goldenAngle = 137.508;
                const colors = [baseColor];
                for (let i = 1; i < 8; i++) {
                    const newH = (h + (goldenAngle * i)) % 360;
                    const satMod = ((i % 3) - 1) * 15;
                    const lightMod = ((i % 4) - 2) * 12;
                    colors.push(createColorVariation(newH, satMod, lightMod));
                }
                return colors;
            })(),

            // Colorblind Safe Categorical (6 colors)
            colorblindSafe: (() => {
                return [
                    hslToHex(25, 75, 45),   // Orange
                    hslToHex(200, 70, 40),  // Blue
                    hslToHex(60, 75, 55),   // Yellow
                    hslToHex(280, 50, 50),  // Purple
                    hslToHex(140, 45, 40),  // Green
                    hslToHex(0, 0, 30)      // Dark gray
                ];
            })(),

            // Clustered Data Viz (10 colors) - Group-friendly families
            clusteredDataViz: (() => {
                const families = [
                    [h, h + 10, h + 20],           // Family 1
                    [h + 120, h + 130, h + 140],   // Family 2
                    [h + 240, h + 250, h + 260],   // Family 3
                    [h + 60]                       // Accent
                ];
                return families.flat().map(hue =>
                    createColorVariation(hue, 5, 0)
                );
            })(),

            // ========== SEMANTIC UI SYSTEM PALETTES ==========

            // Semantic Status Colors (20 colors) - Success/Warning/Error/Info with shades
            semanticStatus: (() => {
                const statuses = [
                    { h: 140, name: 'success' },   // Green
                    { h: 45, name: 'warning' },    // Yellow/Orange
                    { h: 0, name: 'error' },       // Red
                    { h: 210, name: 'info' }       // Blue
                ];
                const colors = [];
                statuses.forEach(status => {
                    for (let i = 0; i < 5; i++) {
                        const lightMod = -30 + (i * 15);
                        colors.push(hslToHex(status.h, 70, 50 + lightMod));
                    }
                });
                return colors;
            })(),

            // Action Oriented (9 colors) - Primary/Secondary/Tertiary with states
            actionOriented: (() => {
                return [
                    baseColor,                          // Primary
                    createColorVariation(h, 10, -15),   // Primary hover
                    createColorVariation(h, -10, -25),  // Primary active
                    createColorVariation(h + 30, -5, 5), // Secondary
                    createColorVariation(h + 30, 5, -10), // Secondary hover
                    createColorVariation(h + 30, -10, -20), // Secondary active
                    createColorVariation(h + 180, 0, 0), // Tertiary
                    createColorVariation(h + 180, 10, -15), // Tertiary hover
                    createColorVariation(h + 180, -10, -25)  // Tertiary active
                ];
            })(),

            // Surface & Border System (12 colors) - Background layers
            surfaceBorderSystem: (() => {
                return [
                    hslToHex(h, 10, 98),  // Surface 1 (lightest)
                    hslToHex(h, 10, 95),  // Surface 2
                    hslToHex(h, 12, 92),  // Surface 3
                    hslToHex(h, 15, 88),  // Surface 4
                    hslToHex(h, 20, 15),  // Dark surface 1
                    hslToHex(h, 20, 12),  // Dark surface 2
                    hslToHex(h, 22, 8),   // Dark surface 3
                    hslToHex(h, 25, 5),   // Dark surface 4
                    hslToHex(h, 20, 85),  // Border light
                    hslToHex(h, 30, 70),  // Border medium
                    hslToHex(h, 40, 25),  // Border dark
                    baseColor             // Accent
                ];
            })(),

            // Focus & Selection States (8 colors)
            focusSelection: (() => {
                return [
                    baseColor,
                    createColorVariation(h, 20, 20),    // Focus ring
                    createColorVariation(h, 30, -10),   // Selected
                    createColorVariation(h, 25, 10),    // Hover
                    createColorVariation(h, -20, 25),   // Disabled
                    createColorVariation(h + 180, 20, -10), // Active
                    createColorVariation(h, 35, -20),   // Pressed
                    createColorVariation(h, 15, 15)     // Highlight
                ];
            })(),

            // ========== CULTURAL & HISTORICAL PALETTES ==========

            // Art Deco (6 colors)
            artDeco: (() => {
                return [
                    hslToHex(45, 80, 50),   // Gold
                    hslToHex(0, 0, 10),     // Black
                    hslToHex(0, 0, 95),     // Cream
                    hslToHex(160, 40, 35),  // Teal
                    hslToHex(25, 70, 45),   // Bronze
                    hslToHex(280, 30, 25)   // Deep purple
                ];
            })(),

            // Bauhaus (7 colors)
            bauhaus: (() => {
                return [
                    hslToHex(0, 85, 50),    // Primary red
                    hslToHex(210, 90, 50),  // Primary blue
                    hslToHex(60, 90, 50),   // Primary yellow
                    hslToHex(0, 0, 20),     // Black
                    hslToHex(0, 0, 95),     // White
                    hslToHex(30, 70, 45),   // Orange
                    hslToHex(280, 60, 45)   // Purple
                ];
            })(),

            // Memphis Design (8 colors)
            memphisDesign: (() => {
                return [
                    hslToHex(330, 90, 60),  // Hot pink
                    hslToHex(180, 85, 50),  // Cyan
                    hslToHex(60, 95, 55),   // Yellow
                    hslToHex(0, 0, 10),     // Black
                    hslToHex(120, 75, 50),  // Green
                    hslToHex(25, 90, 55),   // Orange
                    hslToHex(270, 80, 55),  // Purple
                    hslToHex(200, 70, 45)   // Blue
                ];
            })(),

            // Wes Anderson Film (8 colors)
            wesAnderson: (() => {
                return [
                    hslToHex(35, 65, 55),   // Warm beige
                    hslToHex(10, 70, 50),   // Terracotta
                    hslToHex(50, 60, 65),   // Pastel yellow
                    hslToHex(195, 50, 55),  // Muted teal
                    hslToHex(340, 55, 50),  // Dusty rose
                    hslToHex(25, 75, 45),   // Burnt orange
                    hslToHex(160, 30, 45),  // Sage green
                    hslToHex(220, 40, 40)   // Navy blue
                ];
            })(),

            // Blade Runner Aesthetic (6 colors)
            bladeRunner: (() => {
                return [
                    hslToHex(180, 60, 25),  // Neon teal
                    hslToHex(330, 85, 40),  // Neon pink
                    hslToHex(30, 90, 40),   // Amber
                    hslToHex(0, 0, 10),     // Deep black
                    hslToHex(240, 70, 30),  // Electric blue
                    hslToHex(60, 80, 50)    // Yellow glow
                ];
            })(),

            // ========== NATURE & BIOMIMICRY PALETTES ==========

            // Peacock Feather (8 colors)
            peacockFeather: (() => {
                return [
                    hslToHex(200, 90, 30),  // Deep blue
                    hslToHex(175, 85, 35),  // Teal
                    hslToHex(140, 70, 40),  // Green
                    hslToHex(45, 90, 50),   // Gold
                    hslToHex(280, 50, 25),  // Purple
                    hslToHex(160, 60, 45),  // Emerald
                    hslToHex(30, 80, 40),   // Bronze
                    hslToHex(190, 75, 25)   // Navy teal
                ];
            })(),

            // Coral Reef (10 colors)
            coralReef: (() => {
                return [
                    hslToHex(180, 70, 60),  // Aqua
                    hslToHex(15, 85, 60),   // Coral pink
                    hslToHex(50, 75, 70),   // Sandy yellow
                    hslToHex(280, 60, 55),  // Purple coral
                    hslToHex(30, 90, 55),   // Orange coral
                    hslToHex(190, 80, 40),  // Deep sea blue
                    hslToHex(150, 50, 50),  // Sea green
                    hslToHex(350, 70, 60),  // Pink anemone
                    hslToHex(210, 60, 45),  // Ocean blue
                    hslToHex(40, 65, 75)    // Light sand
                ];
            })(),

            // Desert Biome (8 colors)
            desertBiome: (() => {
                return [
                    hslToHex(35, 50, 65),   // Sand
                    hslToHex(25, 60, 45),   // Adobe
                    hslToHex(15, 70, 35),   // Terracotta
                    hslToHex(45, 40, 75),   // Pale yellow
                    hslToHex(30, 30, 50),   // Tan
                    hslToHex(20, 55, 30),   // Brown
                    hslToHex(200, 20, 60),  // Desert sky
                    hslToHex(140, 35, 35)   // Cactus green
                ];
            })(),

            // Ocean Depths (9 colors)
            oceanDepths: (() => {
                const colors = [];
                for (let i = 0; i < 9; i++) {
                    const depth = 200 + (i * 5);
                    const lightness = 70 - (i * 7);
                    const saturation = 60 + (i * 3);
                    colors.push(hslToHex(depth % 360, saturation, lightness));
                }
                return colors;
            })(),

            // Rainforest Canopy (10 colors)
            rainforestCanopy: (() => {
                return [
                    hslToHex(140, 60, 35),  // Deep jungle green
                    hslToHex(120, 70, 40),  // Forest green
                    hslToHex(100, 50, 45),  // Moss
                    hslToHex(30, 65, 45),   // Tree bark
                    hslToHex(180, 55, 50),  // Stream blue
                    hslToHex(350, 75, 50),  // Tropical flower red
                    hslToHex(280, 60, 45),  // Orchid purple
                    hslToHex(50, 70, 60),   // Banana yellow
                    hslToHex(25, 80, 40),   // Orange bird
                    hslToHex(160, 50, 30)   // Fern green
                ];
            })(),

            // Dawn Sky (8 colors)
            dawnSky: (() => {
                return [
                    hslToHex(240, 40, 20),  // Pre-dawn navy
                    hslToHex(260, 50, 30),  // Deep purple
                    hslToHex(320, 60, 40),  // Magenta
                    hslToHex(350, 70, 50),  // Pink
                    hslToHex(15, 80, 55),   // Coral
                    hslToHex(35, 85, 60),   // Peach
                    hslToHex(45, 90, 70),   // Light yellow
                    hslToHex(55, 70, 80)    // Pale yellow
                ];
            })(),

            // Stormy Weather (7 colors)
            stormyWeather: (() => {
                return [
                    hslToHex(220, 25, 25),  // Storm cloud gray
                    hslToHex(210, 30, 35),  // Rain cloud
                    hslToHex(200, 20, 45),  // Overcast
                    hslToHex(60, 70, 75),   // Lightning yellow
                    hslToHex(240, 40, 20),  // Deep storm
                    hslToHex(190, 30, 30),  // Thunder blue
                    hslToHex(0, 0, 15)      // Night storm
                ];
            })(),

            // ========== MATHEMATICAL SEQUENCE PALETTES ==========

            // Lucas Numbers (8 colors)
            lucasNumbers: (() => {
                const lucas = [2, 1, 3, 4, 7, 11, 18, 29];
                return lucas.map((num, i) => {
                    const hueShift = (num * 15.7) % 360;
                    const satMod = ((num % 5) - 2) * 10;
                    const lightMod = ((num % 4) - 2) * 12;
                    return createColorVariation(h + hueShift, satMod, lightMod);
                });
            })(),

            // Tribonacci (8 colors)
            tribonacci: (() => {
                const trib = [0, 0, 1, 1, 2, 4, 7, 13];
                return trib.map((num, i) => {
                    const hueShift = (num * 31.4) % 360;
                    const satMod = ((num % 3) - 1) * 15;
                    const lightMod = ((num % 5) - 2) * 10;
                    return createColorVariation(h + hueShift, satMod, lightMod);
                });
            })(),

            // Prime Number Steps (10 colors)
            primeSteps: (() => {
                const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29];
                return primes.map(prime => {
                    const hueShift = (prime * 12.3) % 360;
                    const satMod = (prime % 7 - 3) * 8;
                    const lightMod = (prime % 5 - 2) * 10;
                    return createColorVariation(h + hueShift, satMod, lightMod);
                });
            })(),

            // Pythagorean Intervals (8 colors) - Musical ratios applied to color
            pythagoreanIntervals: (() => {
                const ratios = [1, 9/8, 5/4, 4/3, 3/2, 5/3, 15/8, 2];
                return ratios.map(ratio => {
                    const hueShift = (ratio * 180) % 360;
                    const satMod = Math.sin(ratio * Math.PI) * 20;
                    const lightMod = Math.cos(ratio * Math.PI) * 15;
                    return createColorVariation(h + hueShift, satMod, lightMod);
                });
            })(),

            // Pell Numbers (7 colors)
            pellNumbers: (() => {
                const pell = [0, 1, 2, 5, 12, 29, 70];
                return pell.map(num => {
                    const hueShift = (num * 8.5) % 360;
                    const satMod = ((num % 6) - 3) * 12;
                    const lightMod = ((num % 4) - 2) * 15;
                    return createColorVariation(h + hueShift, satMod, lightMod);
                });
            })(),

            // ========== GRADIENT & INTERPOLATION PALETTES ==========

            // Multi-Point Gradient (12 colors) - Smooth 4-color transition
            multiPointGradient: (() => {
                const anchors = [h, h + 90, h + 180, h + 270];
                const colors = [];
                for (let i = 0; i < 12; i++) {
                    const t = i / 11;
                    const segment = Math.floor(t * 3);
                    const localT = (t * 3) - segment;
                    const startH = anchors[segment];
                    const endH = anchors[Math.min(segment + 1, 3)];
                    const interpH = startH + (endH - startH) * localT;
                    const satMod = Math.sin(t * Math.PI * 2) * 15;
                    const lightMod = Math.cos(t * Math.PI) * 20;
                    colors.push(createColorVariation(interpH, satMod, lightMod));
                }
                return colors;
            })(),

            // Ease-In Gradient (10 colors)
            easeInGradient: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const t = i / 9;
                    const eased = t * t; // Quadratic ease-in
                    const interpH = h + (eased * 180);
                    const lightMod = -30 + (eased * 60);
                    colors.push(createColorVariation(interpH, 0, lightMod));
                }
                return colors;
            })(),

            // Ease-Out Gradient (10 colors)
            easeOutGradient: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const t = i / 9;
                    const eased = 1 - (1 - t) * (1 - t); // Quadratic ease-out
                    const interpH = h + (eased * 180);
                    const lightMod = -30 + (eased * 60);
                    colors.push(createColorVariation(interpH, 0, lightMod));
                }
                return colors;
            })(),

            // Radial Color Expansion (12 colors) - Rings expanding outward
            radialExpansion: (() => {
                const colors = [baseColor];
                for (let ring = 1; ring <= 3; ring++) {
                    const angleStep = 360 / (ring * 3);
                    for (let i = 0; i < ring * 3; i++) {
                        const angle = i * angleStep;
                        const satMod = -ring * 10;
                        const lightMod = ring * 8;
                        colors.push(createColorVariation(h + angle, satMod, lightMod));
                    }
                }
                return colors.slice(0, 12);
            })(),

            // Chroma Scale Fixed Hue (10 colors)
            chromaScale: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const saturation = 10 + (i * 9);
                    colors.push(hslToHex(h, saturation, l));
                }
                return colors;
            })(),

            // ========== EXPERIMENTAL & ARTISTIC PALETTES ==========

            // Duotone (6 colors)
            duotone: (() => {
                const shadow = createColorVariation(h + 30, -20, -35);
                const highlight = createColorVariation(h + 30, -20, 35);
                return [
                    shadow,
                    createColorVariation(h + 30, -15, -20),
                    createColorVariation(h + 30, -10, -5),
                    createColorVariation(h + 30, -10, 5),
                    createColorVariation(h + 30, -15, 20),
                    highlight
                ];
            })(),

            // Tritone (9 colors)
            tritone: (() => {
                const colors = [];
                const hues = [h, h + 120, h + 240];
                hues.forEach(hue => {
                    colors.push(createColorVariation(hue, -10, -25));
                    colors.push(createColorVariation(hue, 0, 0));
                    colors.push(createColorVariation(hue, -10, 25));
                });
                return colors;
            })(),

            // Ombre Series (12 colors) - Smooth single-direction fade
            ombreSeries: (() => {
                const colors = [];
                for (let i = 0; i < 12; i++) {
                    const lightness = Math.max(10, Math.min(90, 85 - (i * 6)));
                    const saturation = Math.max(10, Math.min(90, s + Math.sin((i / 11) * Math.PI) * 20));
                    colors.push(hslToHex(h, saturation, lightness));
                }
                return colors;
            })(),

            // Maximalist (16 colors) - High saturation, high contrast
            maximalist: (() => {
                const colors = [];
                for (let i = 0; i < 16; i++) {
                    const hueShift = (i * 22.5);
                    const saturation = 85 + (i % 3) * 5;
                    const lightness = 35 + (i % 5) * 10;
                    colors.push(hslToHex((h + hueShift) % 360, saturation, lightness));
                }
                return colors;
            })(),

            // Monochrome Plus One (8 colors)
            monochromePlusOne: (() => {
                const grays = [];
                for (let i = 0; i < 7; i++) {
                    grays.push(hslToHex(0, 0, 10 + (i * 13)));
                }
                grays.push(baseColor); // The "one" accent color
                return grays;
            })(),

            // Neon Rainbow (10 colors)
            neonRainbow: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const hueShift = i * 36;
                    colors.push(hslToHex((h + hueShift) % 360, 100, 50));
                }
                return colors;
            })(),

            // Pastel Rainbow (10 colors)
            pastelRainbow: (() => {
                const colors = [];
                for (let i = 0; i < 10; i++) {
                    const hueShift = i * 36;
                    colors.push(hslToHex((h + hueShift) % 360, 35, 75));
                }
                return colors;
            })(),

            // Earth Tones Extended (10 colors)
            earthTonesExtended: (() => {
                return [
                    hslToHex(30, 40, 35),   // Brown
                    hslToHex(35, 45, 50),   // Tan
                    hslToHex(25, 50, 30),   // Dark brown
                    hslToHex(40, 35, 60),   // Beige
                    hslToHex(20, 55, 25),   // Umber
                    hslToHex(45, 40, 55),   // Sand
                    hslToHex(15, 60, 35),   // Sienna
                    hslToHex(30, 30, 45),   // Taupe
                    hslToHex(35, 50, 40),   // Ochre
                    hslToHex(25, 45, 20)    // Dark earth
                ];
            })(),

            // Compound Harmony (12 colors) - Multiple harmonies combined
            compoundHarmony: (() => {
                return [
                    baseColor,
                    createColorVariation(h + 30, 0, 0),    // Analogous
                    createColorVariation(h - 30, 0, 0),    // Analogous
                    createColorVariation(h + 180, 0, 0),   // Complementary
                    createColorVariation(h + 120, 0, 0),   // Triadic
                    createColorVariation(h + 240, 0, 0),   // Triadic
                    createColorVariation(h + 150, 0, 0),   // Split-comp
                    createColorVariation(h + 210, 0, 0),   // Split-comp
                    createColorVariation(h, 0, 20),        // Tint
                    createColorVariation(h, 0, -20),       // Shade
                    createColorVariation(h, -30, 0),       // Tone
                    createColorVariation(h, 30, 0)         // Vivid
                ];
            })(),

            // Simultaneous Contrast (6 colors) - Colors that enhance when adjacent
            simultaneousContrast: (() => {
                return [
                    baseColor,
                    createColorVariation(h + 180, 0, 0),   // Complementary
                    createColorVariation(h, 0, 30),        // Lighter version
                    createColorVariation(h + 180, 0, 30),  // Lighter complement
                    createColorVariation(h, 0, -30),       // Darker version
                    createColorVariation(h + 180, 0, -30)  // Darker complement
                ];
            })()
        };
    };

    // Generate CSS for a specific palette
    const generatePaletteCss = (colors, paletteName) => {
        const cssVarName = paletteName.toLowerCase().replace(/\s+/g, '-');
        return `:root {\n${colors.map((color, i) => `  --${cssVarName}-${i + 1}: ${color};`).join('\n')}\n}\n\n/* Usage examples */\n.${cssVarName}-primary { color: var(--${cssVarName}-1); }\n.${cssVarName}-bg { background-color: var(--${cssVarName}-1); }`;
    };

    // Generate SVG for a specific palette
    const generatePaletteSvg = (colors, paletteName) => {
        const width = colors.length * 60;
        const height = 80;

        return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <!-- ${paletteName} Palette -->
  <rect width="${width}" height="${height}" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8"/>
  ${colors.map((color, i) => `
  <rect x="${i * 60 + 10}" y="10" width="50" height="50" fill="${color}" stroke="#fff" stroke-width="2" rx="4"/>
  <text x="${i * 60 + 35}" y="72" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="#666">${color}</text>`).join('')}
</svg>`;
    };

    const getPaletteName = (key) => {
        const nameMap = {
            // Existing palettes
            goldenRatio: 'Golden Ratio Harmony',
            doubleComplementary: 'Double Complementary Extended',
            rainbowSpectrum: 'Rainbow Spectrum Expansion',
            advancedTriadic: 'Advanced Triadic Plus',
            fibonacci: 'Fibonacci Harmony',
            warmCoolBridge: 'Warm-Cool Gradient Bridge',
            squareHarmony: 'Square Harmony Extended',
            pentadHarmony: 'Pentad Harmony',
            natureInspired: 'Nature Inspired',
            vintageFilm: 'Vintage Film',
            neonCyberpunk: 'Neon Cyberpunk',
            pastelDream: 'Pastel Dream',
            monochromaticExtended: 'Monochromatic Extended',
            analogousExtended: 'Analogous Extended',
            splitComplementaryPlus: 'Split Complementary Plus',
            materialDesign: 'Material Design Inspired',
            hexadic: 'Hexadic Harmony',
            neutralAccent: 'Neutral with Accent',
            saturationGradient: 'Saturation Gradient',
            sixtyThirtyTen: '60-30-10 Rule',
            tintsToneShades: 'Tints, Tones & Shades',
            warmPalette: 'Warm Temperature',
            coolPalette: 'Cool Temperature',
            accentedAnalogous: 'Accented Analogous',
            nearComplementary: 'Near-Complementary',
            springPalette: 'Spring Season',
            summerPalette: 'Summer Season',
            autumnPalette: 'Autumn Season',
            winterPalette: 'Winter Season',
            brandSystem: 'Brand System',
            // Perceptual & Science
            oklchUniform: 'OKLCH Uniform Spacing',
            oklchLightnessScale: 'OKLCH Lightness Scale',
            labGradient: 'LAB Color Space Gradient',
            bezierCurve: 'Bezier Curve Interpolation',
            maximumChroma: 'Maximum Chroma',
            ittenContrast: 'Itten Contrast Harmonies',
            // Data Visualization
            sequentialSingleHue: 'Sequential Single Hue',
            categoricalChevron: 'Categorical Chevron',
            divergingPalette: 'Diverging Palette',
            qualitativeMaxDist: 'Qualitative Maximum Distance',
            colorblindSafe: 'Colorblind Safe Categorical',
            clusteredDataViz: 'Clustered Data Viz',
            // Semantic UI
            semanticStatus: 'Semantic Status Colors',
            actionOriented: 'Action Oriented',
            surfaceBorderSystem: 'Surface & Border System',
            focusSelection: 'Focus & Selection States',
            // Cultural & Historical
            artDeco: 'Art Deco',
            bauhaus: 'Bauhaus',
            memphisDesign: 'Memphis Design',
            wesAnderson: 'Wes Anderson Film',
            bladeRunner: 'Blade Runner',
            // Nature & Biomimicry
            peacockFeather: 'Peacock Feather',
            coralReef: 'Coral Reef',
            desertBiome: 'Desert Biome',
            oceanDepths: 'Ocean Depths',
            rainforestCanopy: 'Rainforest Canopy',
            dawnSky: 'Dawn Sky',
            stormyWeather: 'Stormy Weather',
            // Mathematical
            lucasNumbers: 'Lucas Numbers',
            tribonacci: 'Tribonacci',
            primeSteps: 'Prime Number Steps',
            pythagoreanIntervals: 'Pythagorean Intervals',
            pellNumbers: 'Pell Numbers',
            // Gradients & Interpolation
            multiPointGradient: 'Multi-Point Gradient',
            easeInGradient: 'Ease-In Gradient',
            easeOutGradient: 'Ease-Out Gradient',
            radialExpansion: 'Radial Color Expansion',
            chromaScale: 'Chroma Scale',
            // Experimental
            duotone: 'Duotone',
            tritone: 'Tritone',
            ombreSeries: 'Ombre Series',
            maximalist: 'Maximalist',
            monochromePlusOne: 'Monochrome Plus One',
            neonRainbow: 'Neon Rainbow',
            pastelRainbow: 'Pastel Rainbow',
            earthTonesExtended: 'Earth Tones Extended',
            compoundHarmony: 'Compound Harmony',
            simultaneousContrast: 'Simultaneous Contrast'
        };
        return nameMap[key] || toSentenceCase(key);
    };

    const getPaletteDescription = (key) => {
        const descriptions = {
            // Harmonies
            goldenRatio: 'Based on the golden angle (137.508°), creates natural-feeling distributions similar to phyllotaxis patterns in nature',
            fibonacci: 'Uses Fibonacci sequence (1,1,2,3,5,8...) to determine hue shifts, creating organically balanced color relationships',
            hexadic: '6 evenly-spaced colors (60° apart) on the color wheel, creates vibrant, energetic palettes',
            squareHarmony: '4 colors 90° apart with variations, provides strong visual interest with balanced contrast',
            pentadHarmony: '5 colors arranged in a pentagon (72° apart), offers dynamic balance with rich variety',
            doubleComplementary: 'Two sets of complementary pairs with variations, creates complex harmonies with strong contrast',
            advancedTriadic: 'Triadic harmony (120° spacing) with light/dark variations for each hue',
            splitComplementaryPlus: 'Base color with two adjacent to its complement, offers strong contrast with less tension',
            accentedAnalogous: 'Adjacent colors on the wheel with a complementary accent for visual pop',
            nearComplementary: 'Colors close to complementary (165° & 195°) for subtler contrast than true opposites',
            rainbowSpectrum: 'Full spectrum traversal with wavelike saturation/lightness variations',
            warmCoolBridge: 'Smooth 180° transition from warm to cool hues, connects opposite temperature zones',

            // Design Systems
            materialDesign: 'Inspired by Google Material Design 3.0 palette structure with tonal variations',
            brandSystem: 'Complete brand identity system with primary, secondary, tertiary, and accent colors',
            sixtyThirtyTen: 'Interior design rule: 60% dominant, 30% secondary, 10% accent colors',
            neutralAccent: 'Low-saturation neutrals with vibrant accent colors for clean, focused designs',
            monochromaticExtended: 'Single hue with wavelike saturation variations across light to dark',
            analogousExtended: '120° spread of adjacent colors with sinusoidal modulation',
            saturationGradient: 'Fixed hue and lightness with saturation ramping from 10% to 100%',
            tintsToneShades: 'Color theory fundamentals: tints (+ white), tones (+ gray), shades (+ black)',
            semanticStatus: 'UI status colors with 5 shades each: success, warning, error, info',
            actionOriented: 'Button states: primary, secondary, tertiary with hover/active variations',
            surfaceBorderSystem: 'Background elevation system for light/dark modes with border colors',
            focusSelection: 'Interactive states: focus rings, selection, hover, disabled, pressed, active',

            // Scientific
            oklchUniform: 'OKLCH color space with perceptually uniform 36° hue steps (based on Oklab)',
            oklchLightnessScale: 'Perceptually uniform lightness scale in OKLCH space, each step feels equally different',
            labGradient: 'CIE LAB color space gradient, ensures perceptually linear lightness transitions',
            bezierCurve: 'Cubic Bezier curve interpolation through color space for smooth, designer-controlled transitions',
            maximumChroma: 'Pure, fully saturated hues at maximum chroma (100% saturation, 50% lightness)',
            ittenContrast: 'Based on Johannes Itten\'s 7 contrasts: hue, light-dark, warm-cool, complementary, saturation',
            sequentialSingleHue: 'Data visualization palette for ordered data, lightness decreases sequentially',
            categoricalChevron: 'Maximum perceptual distance for categorical data, alternating pattern for distinction',
            divergingPalette: 'Two-directional palette from neutral center, ideal for showing deviation from a midpoint',
            qualitativeMaxDist: 'Golden angle distribution ensures maximum perceptual distance between colors',
            colorblindSafe: 'Carefully selected hues distinguishable by protanopes, deuteranopes, and tritanopes',
            clusteredDataViz: 'Color families for grouped data, each group has related hues',
            multiPointGradient: '4-anchor smooth gradient with sinusoidal modulation',
            easeInGradient: 'Quadratic ease-in function applied to hue/lightness for accelerating transition',
            easeOutGradient: 'Quadratic ease-out function for decelerating color transition',
            radialExpansion: 'Colors arranged in concentric rings expanding outward from base',
            chromaScale: 'Saturation ramp from 10% to 100% while maintaining constant hue and lightness',

            // Thematic
            springPalette: 'Light, bright colors evoking cherry blossoms, fresh grass, and spring flowers',
            summerPalette: 'Muted, light colors reminiscent of hazy summer days and beach tones',
            autumnPalette: 'Dark, muted earth tones capturing falling leaves and harvest season',
            winterPalette: 'Cool, high-contrast colors evoking ice, snow, and crisp winter air',
            warmPalette: 'Red, orange, yellow spectrum creating cozy, energetic feelings',
            coolPalette: 'Blue, green, purple spectrum for calming, refreshing atmospheres',
            natureInspired: 'Colors representing natural elements: sky, forest, ocean, sunset, earth',
            vintageFilm: 'Desaturated, amber-shifted palette mimicking aged film and photographs',
            neonCyberpunk: 'Electric, highly saturated colors inspired by cyberpunk aesthetics and neon signage',
            pastelDream: 'Soft, desaturated rainbow creating gentle, dreamy atmospheres',

            // Mathematical
            lucasNumbers: 'Lucas sequence (2,1,3,4,7,11,18,29...) applied to hue distribution',
            tribonacci: 'Tribonacci sequence (sum of previous 3 numbers) creates organic spacing',
            primeSteps: 'Prime numbers (2,3,5,7,11,13...) determine hue shifts for mathematical elegance',
            pythagoreanIntervals: 'Musical frequency ratios from Pythagorean tuning applied to color relationships',
            pellNumbers: 'Pell sequence creates unique mathematical progression in color space',

            // Artistic
            duotone: 'Two-color palette with gradual transitions, popular in photography and print',
            tritone: 'Three primary hues with light/dark variations, vintage printing technique',
            ombreSeries: 'Smooth fade from light to dark in a single hue with wavelike saturation',
            maximalist: 'High saturation, high contrast, 16 colors for bold, maximalist aesthetics',
            monochromePlusOne: 'Grayscale palette with a single accent color for minimal, focused design',
            neonRainbow: 'Full spectrum at maximum saturation (100% sat, 50% lightness)',
            pastelRainbow: 'Full spectrum in soft pastels (35% sat, 75% lightness)',
            compoundHarmony: 'Combines multiple harmony types: analogous, complementary, triadic, split-comp',
            simultaneousContrast: 'Colors that optically enhance each other when placed adjacent',

            // Static Palettes
            colorblindSafe: 'Fixed palette: Orange, Blue, Yellow, Purple, Green, Dark Gray - safe for all color vision types',
            artDeco: 'Fixed palette: Gold, Black, Cream, Teal, Bronze, Purple - 1920s Art Deco movement',
            bauhaus: 'Fixed palette: Primary Red, Blue, Yellow + Black, White - Bauhaus design school (1919-1933)',
            memphisDesign: 'Fixed palette: Hot Pink, Cyan, Yellow, Black, Green, Orange, Purple - Memphis Group (1980s)',
            wesAnderson: 'Fixed palette: Warm beige, terracotta, teal, dusty rose - Wes Anderson film aesthetic',
            bladeRunner: 'Fixed palette: Neon teal, pink, amber, black, electric blue - Blade Runner (1982) cinematography',
            peacockFeather: 'Fixed palette: Deep blue, teal, green, gold, purple, emerald, bronze - iridescent peacock colors',
            coralReef: 'Fixed palette: Aqua, coral pink, sandy yellow, purple, orange, sea blue - tropical reef ecosystem',
            desertBiome: 'Fixed palette: Sand, adobe, terracotta, pale yellow, tan, brown, desert sky - arid landscapes',
            oceanDepths: 'Fixed palette: Progressive darkening blues from surface to deep ocean',
            rainforestCanopy: 'Fixed palette: Jungle greens, tree bark, stream blue, tropical flowers - rainforest biodiversity',
            dawnSky: 'Fixed palette: Pre-dawn navy through magenta, pink, coral to pale yellow - sunrise progression',
            stormyWeather: 'Fixed palette: Storm clouds, rain, overcast, lightning, thunder blue - dramatic weather',
            earthTonesExtended: 'Fixed palette: Browns, tans, beiges, umbers, siennas, taupes, ochres - natural earth pigments'
        };
        return descriptions[key] || 'Custom color palette based on color theory principles';
    };

    // Categorize palettes for different tabs
    const categorizePalettes = (allHarmonies) => {
        return {
            harmonies: {
                goldenRatio: allHarmonies.goldenRatio,
                fibonacci: allHarmonies.fibonacci,
                hexadic: allHarmonies.hexadic,
                squareHarmony: allHarmonies.squareHarmony,
                pentadHarmony: allHarmonies.pentadHarmony,
                doubleComplementary: allHarmonies.doubleComplementary,
                advancedTriadic: allHarmonies.advancedTriadic,
                splitComplementaryPlus: allHarmonies.splitComplementaryPlus,
                accentedAnalogous: allHarmonies.accentedAnalogous,
                nearComplementary: allHarmonies.nearComplementary,
                rainbowSpectrum: allHarmonies.rainbowSpectrum,
                warmCoolBridge: allHarmonies.warmCoolBridge
            },
            designSystems: {
                materialDesign: allHarmonies.materialDesign,
                brandSystem: allHarmonies.brandSystem,
                sixtyThirtyTen: allHarmonies.sixtyThirtyTen,
                neutralAccent: allHarmonies.neutralAccent,
                monochromaticExtended: allHarmonies.monochromaticExtended,
                analogousExtended: allHarmonies.analogousExtended,
                saturationGradient: allHarmonies.saturationGradient,
                tintsToneShades: allHarmonies.tintsToneShades,
                semanticStatus: allHarmonies.semanticStatus,
                actionOriented: allHarmonies.actionOriented,
                surfaceBorderSystem: allHarmonies.surfaceBorderSystem,
                focusSelection: allHarmonies.focusSelection
            },
            scientific: {
                sequentialSingleHue: allHarmonies.sequentialSingleHue,
                categoricalChevron: allHarmonies.categoricalChevron,
                divergingPalette: allHarmonies.divergingPalette,
                qualitativeMaxDist: allHarmonies.qualitativeMaxDist,
                clusteredDataViz: allHarmonies.clusteredDataViz,
                oklchUniform: allHarmonies.oklchUniform,
                oklchLightnessScale: allHarmonies.oklchLightnessScale,
                labGradient: allHarmonies.labGradient,
                bezierCurve: allHarmonies.bezierCurve,
                maximumChroma: allHarmonies.maximumChroma,
                ittenContrast: allHarmonies.ittenContrast,
                multiPointGradient: allHarmonies.multiPointGradient,
                easeInGradient: allHarmonies.easeInGradient,
                easeOutGradient: allHarmonies.easeOutGradient,
                radialExpansion: allHarmonies.radialExpansion,
                chromaScale: allHarmonies.chromaScale
            },
            thematic: {
                springPalette: allHarmonies.springPalette,
                summerPalette: allHarmonies.summerPalette,
                autumnPalette: allHarmonies.autumnPalette,
                winterPalette: allHarmonies.winterPalette,
                warmPalette: allHarmonies.warmPalette,
                coolPalette: allHarmonies.coolPalette,
                natureInspired: allHarmonies.natureInspired,
                vintageFilm: allHarmonies.vintageFilm,
                neonCyberpunk: allHarmonies.neonCyberpunk,
                pastelDream: allHarmonies.pastelDream
            },
            natural: {
                lucasNumbers: allHarmonies.lucasNumbers,
                tribonacci: allHarmonies.tribonacci,
                primeSteps: allHarmonies.primeSteps,
                pythagoreanIntervals: allHarmonies.pythagoreanIntervals,
                pellNumbers: allHarmonies.pellNumbers
            },
            artistic: {
                duotone: allHarmonies.duotone,
                tritone: allHarmonies.tritone,
                ombreSeries: allHarmonies.ombreSeries,
                maximalist: allHarmonies.maximalist,
                monochromePlusOne: allHarmonies.monochromePlusOne,
                neonRainbow: allHarmonies.neonRainbow,
                pastelRainbow: allHarmonies.pastelRainbow,
                compoundHarmony: allHarmonies.compoundHarmony,
                simultaneousContrast: allHarmonies.simultaneousContrast
            },
            static: {
                colorblindSafe: allHarmonies.colorblindSafe,
                artDeco: allHarmonies.artDeco,
                bauhaus: allHarmonies.bauhaus,
                memphisDesign: allHarmonies.memphisDesign,
                wesAnderson: allHarmonies.wesAnderson,
                bladeRunner: allHarmonies.bladeRunner,
                peacockFeather: allHarmonies.peacockFeather,
                coralReef: allHarmonies.coralReef,
                desertBiome: allHarmonies.desertBiome,
                oceanDepths: allHarmonies.oceanDepths,
                rainforestCanopy: allHarmonies.rainforestCanopy,
                dawnSky: allHarmonies.dawnSky,
                stormyWeather: allHarmonies.stormyWeather,
                earthTonesExtended: allHarmonies.earthTonesExtended
            }
        };
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'palettes':
                return (
                    <div data-palette-tabs className="space-y-6 overflow-visible">
                        {/* Shades Display */}
                        {shades.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                Color Shades
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Complete lightness range
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => savePalette('Color Shades', shades.map(s => s.color))}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                            >
                                                <Bookmark className="w-3 h-3" />
                                                Save
                                            </button>
                                            <button
                                                onClick={(event) => handleExportToVisualizer(shades.map(s => s.color), 'Color Shades', event)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Visualizer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <ShadesDisplay
                                        shades={shades}
                                        selectedColor={selectedColor}
                                        handleColorClick={onColorClick}
                                        visibleFields={visibleFields}
                                        colorBlindnessType={colorBlindnessType}
                                        isDark={isDark}
                                        addColorToPalette={addColorToPalette}
                                        addColorToCustomPalette={addColorToCustomPalette}
                                    />
                                </div>
                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                                    <CompactExportDropdown
                                        colors={shades.map(s => s.color)}
                                        paletteName="Color Shades"
                                        getCss={() => generatePaletteCss(shades.map(s => s.color), 'Color Shades')}
                                        getSvg={() => generatePaletteSvg(shades.map(s => s.color), 'Color Shades')}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Generated Palettes with new styling */}
                        {Object.entries(palettes).map(([key, colors]) => (
                            <div key={key} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {toSentenceCase(key)}
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {colors.length} colors
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => savePalette(toSentenceCase(key), colors)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                            >
                                                <Bookmark className="w-3 h-3" />
                                                Save
                                            </button>
                                            <button
                                                onClick={(event) => handleExportToVisualizer(colors, getPaletteName(key), event)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Visualizer
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4">
                                    <PaletteDisplay
                                        colors={colors}
                                        onColorClick={onColorClick}
                                        baseColor={selectedColor}
                                        visibleFields={visibleFields}
                                        colorBlindnessType={colorBlindnessType}
                                        isDark={isDark}
                                        addColorToPalette={addColorToPalette}
                                        addColorToCustomPalette={addColorToCustomPalette}
                                    />
                                </div>

                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                                    <CompactExportDropdown
                                        colors={colors}
                                        paletteName={toSentenceCase(key)}
                                        getCss={() => generatePaletteCss(colors, toSentenceCase(key))}
                                        getSvg={() => generatePaletteSvg(colors, toSentenceCase(key))}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'harmonies':
            case 'designSystems':
            case 'scientific':
            case 'thematic':
            case 'natural':
            case 'artistic':
            case 'static':
                const allHarmonies = generateAdvancedHarmonies(selectedColor);
                const categorized = categorizePalettes(allHarmonies);
                const currentCategory = activeTab === 'harmonies' ? categorized.harmonies
                    : activeTab === 'designSystems' ? categorized.designSystems
                    : activeTab === 'scientific' ? categorized.scientific
                    : activeTab === 'thematic' ? categorized.thematic
                    : activeTab === 'natural' ? categorized.natural
                    : activeTab === 'artistic' ? categorized.artistic
                    : categorized.static;

                return (
                    <div className="space-y-6 overflow-visible">
                        {Object.entries(currentCategory).map(([key, colors]) => (
                            <div key={key} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible">
                                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {getPaletteName(key)}
                                                </h4>
                                                <Tooltip content={getPaletteDescription(key)}>
                                                    <HelpCircle className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                                                </Tooltip>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {colors.length} colors
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => savePalette(getPaletteName(key), colors)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                            >
                                                <Bookmark className="w-3 h-3" />
                                                Save
                                            </button>
                                            <button
                                                onClick={(event) => handleExportToVisualizer(colors, getPaletteName(key), event)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Visualizer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <PaletteDisplay
                                        colors={colors}
                                        onColorClick={onColorClick}
                                        baseColor={selectedColor}
                                        visibleFields={visibleFields}
                                        colorBlindnessType={colorBlindnessType}
                                        isDark={isDark}
                                        addColorToPalette={addColorToPalette}
                                        addColorToCustomPalette={addColorToCustomPalette}
                                    />
                                </div>
                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                                    <CompactExportDropdown
                                        colors={colors}
                                        paletteName={getPaletteName(key)}
                                        getCss={() => generatePaletteCss(colors, getPaletteName(key))}
                                        getSvg={() => generatePaletteSvg(colors, getPaletteName(key))}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                );

            default:
                return null;
        }
    };

    const paletteCount = Object.keys(palettes).length;

    return (
        <div className="overflow-visible">
            <PageHeader
                title="Generated Palettes"
                description="Harmony-based color palettes and shade variations"
                tabs={[
                    { id: 'palettes', label: 'Palettes', count: paletteCount + (shades.length > 0 ? 1 : 0) },
                    { id: 'harmonies', label: 'Harmonies', count: 12 },
                    { id: 'designSystems', label: 'Design Systems', count: 12 },
                    { id: 'scientific', label: 'Scientific', count: 16 },
                    { id: 'thematic', label: 'Thematic', count: 10 },
                    { id: 'natural', label: 'Mathematical', count: 5 },
                    { id: 'artistic', label: 'Artistic', count: 9 },
                    { id: 'static', label: 'Static Palettes', count: 14 }
                ]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            {/* Tab Content */}
            <div className="overflow-visible">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default PaletteTabs;